import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import supabase from '../lib/supabase';

const { FiPlus, FiTrash2, FiArrowLeft, FiLoader } = FiIcons;

const CreateSession = () => {
  const navigate = useNavigate();
  const [words, setWords] = useState(['']);
  const [completionMessage, setCompletionMessage] = useState('Glückwunsch! Du hast alle Wörter gefunden!');
  const [gridSize, setGridSize] = useState(15);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const addWordField = () => {
    if (words.length < 15) {
      setWords([...words, '']);
    }
  };

  const removeWordField = (index) => {
    if (words.length > 1) {
      setWords(words.filter((_, i) => i !== index));
    }
  };

  const updateWord = (index, value) => {
    const newWords = [...words];
    newWords[index] = value.toUpperCase();
    setWords(newWords);
  };

  const generateGrid = (validWords, size) => {
    // Word search grid generator
    const grid = Array(size).fill(null).map(() => Array(size).fill(''));
    const placedWords = [];
    
    const directions = [
      { dx: 1, dy: 0 },   // horizontal
      { dx: 0, dy: 1 },   // vertical
      { dx: 1, dy: 1 },   // diagonal down-right
      { dx: 1, dy: -1 },  // diagonal up-right
    ];

    // Place words
    for (const word of validWords) {
      let placed = false;
      let attempts = 0;
      const maxAttempts = 100;

      while (!placed && attempts < maxAttempts) {
        const direction = directions[Math.floor(Math.random() * directions.length)];
        const startX = Math.floor(Math.random() * size);
        const startY = Math.floor(Math.random() * size);

        if (canPlaceWord(word, startX, startY, direction, grid, size)) {
          placeWordAt(word, startX, startY, direction, grid);
          placed = true;
        }
        attempts++;
      }
    }

    // Fill empty cells
    const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        if (grid[y][x] === '') {
          grid[y][x] = letters[Math.floor(Math.random() * letters.length)];
        }
      }
    }

    return grid;
  };

  const canPlaceWord = (word, startX, startY, direction, grid, size) => {
    for (let i = 0; i < word.length; i++) {
      const x = startX + (direction.dx * i);
      const y = startY + (direction.dy * i);
      
      if (x < 0 || x >= size || y < 0 || y >= size) {
        return false;
      }
      
      const currentCell = grid[y][x];
      if (currentCell !== '' && currentCell !== word[i]) {
        return false;
      }
    }
    return true;
  };

  const placeWordAt = (word, startX, startY, direction, grid) => {
    for (let i = 0; i < word.length; i++) {
      const x = startX + (direction.dx * i);
      const y = startY + (direction.dy * i);
      grid[y][x] = word[i];
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const validWords = words.filter(word => word.trim().length >= 3);
    
    if (validWords.length < 3) {
      setError('Mindestens 3 Wörter mit je mindestens 3 Buchstaben erforderlich.');
      setLoading(false);
      return;
    }

    try {
      const grid = generateGrid(validWords, parseInt(gridSize));
      
      const { data, error: supabaseError } = await supabase
        .from('game_sessions_bs7x9k2m1q')
        .insert({
          words: validWords,
          grid: grid,
          completion_message: completionMessage,
          grid_size: parseInt(gridSize),
          found_words: [],
          created_at: new Date().toISOString(),
          last_activity: new Date().toISOString()
        })
        .select()
        .single();

      if (supabaseError) throw supabaseError;

      // Store host key for this session
      localStorage.setItem(`host-${data.id}`, 'host-key');
      
      navigate(`/session/${data.id}`);
    } catch (err) {
      console.error('Error creating session:', err);
      setError('Fehler beim Erstellen der Session. Bitte versuche es erneut.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white py-8">
      <div className="container mx-auto px-4 max-w-2xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-[#2b6777] hover:opacity-70 mb-6 transition-opacity font-rubik"
          >
            <SafeIcon icon={FiArrowLeft} />
            Zurück
          </button>

          <div className="bg-white rounded-lg shadow-lg p-8 border border-gray-200">
            <h1 className="text-3xl font-bold text-[#2b6777] mb-8 text-center font-rubik">
              Neues Buchstabensalat-Spiel erstellen
            </h1>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-[#2b6777] mb-3 font-rubik">
                  Zu findende Wörter (3-15 Wörter)
                </label>
                <div className="space-y-3">
                  {words.map((word, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        value={word}
                        onChange={(e) => updateWord(index, e.target.value)}
                        placeholder={`Wort ${index + 1}`}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#2b6777] focus:border-transparent text-[#2b6777] font-rubik"
                        maxLength="20"
                      />
                      {words.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeWordField(index)}
                          className="px-3 py-2 text-red-600 hover:text-red-800 transition-colors"
                        >
                          <SafeIcon icon={FiTrash2} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                {words.length < 15 && (
                  <button
                    type="button"
                    onClick={addWordField}
                    className="mt-3 flex items-center gap-2 text-[#2b6777] hover:opacity-70 transition-opacity font-rubik"
                  >
                    <SafeIcon icon={FiPlus} />
                    Weiteres Wort hinzufügen
                  </button>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-[#2b6777] mb-2 font-rubik">
                  Gittergröße
                </label>
                <select
                  value={gridSize}
                  onChange={(e) => setGridSize(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#2b6777] focus:border-transparent text-[#2b6777] font-rubik"
                >
                  <option value="10">10x10</option>
                  <option value="15">15x15</option>
                  <option value="20">20x20</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#2b6777] mb-2 font-rubik">
                  Nachricht bei Vollendung
                </label>
                <textarea
                  value={completionMessage}
                  onChange={(e) => setCompletionMessage(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#2b6777] focus:border-transparent text-[#2b6777] font-rubik"
                  rows="3"
                  maxLength="200"
                />
              </div>

              {error && (
                <div className="text-red-600 text-sm bg-red-50 p-3 rounded-md font-rubik">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#c8d8e4] hover:bg-[#b5c7d1] disabled:opacity-50 text-white py-3 px-4 rounded-md font-semibold transition-colors duration-200 flex items-center justify-center gap-2 font-rubik"
              >
                {loading ? (
                  <>
                    <SafeIcon icon={FiLoader} className="animate-spin" />
                    Erstelle Spiel...
                  </>
                ) : (
                  'Spiel erstellen'
                )}
              </button>
            </form>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default CreateSession;